import { z } from 'zod';

import {
  hasItemsSelected,
  type WorkflowData,
  type StepBaseConfig,
  type StepConfig,
  type Steps,
  type TaskConfig,
  type Tasks,
  type TaskSchemaConfig,
  type Workflow,
} from './schemaTypes';
import { isTaskEnabled } from './taskMode';
import { clientLogger } from '../utils/logger';
import { resolveTaskGuards } from '../utils/tasks';
import type { TaskRuntimeContext } from './types';
import { ItemSelectionTaskSchema } from '../components/tasks/ItemSelectionTask/ItemSelectionTaskSchema';
import { MethodTaskSchema } from '../components/tasks/MethodTask/MethodTaskSchema';
import { ReasonsTaskSchema } from '../components/tasks/ReasonsTask/ReasonsTaskSchema';
import { RefundTaskSchema } from '../components/tasks/RefundTask/RefundTaskSchema';
import { ReturnPolicyTaskSchema } from '../components/tasks/ReturnPolicyTask/ReturnPolicyTaskSchema';
import { ResolutionTaskSchema } from '../components/tasks/ResolutionTask/ResolutionTaskSchema';
import { SignatureTaskSchema } from '../components/tasks/SignatureTask/SignatureTaskSchema';
import { OrderlessCustomerFormSchema } from '../components/tasks/OrderlessTask/OrderlessTaskSchema';
import { OrderlessDisclaimerTaskSchema } from '../components/tasks/OrderlessDisclaimerTask/OrderlessDisclaimerTaskSchema';
import { GiftEmailTaskSchema } from '../components/tasks/GiftEmailTask/GiftEmailTaskSchema';

// Task Configurations

const ItemSelectionTask: TaskSchemaConfig = {
  id: 'itemSelection',
  schema: ItemSelectionTaskSchema,
};

const MethodTask: TaskSchemaConfig = {
  id: 'returnMethod',
  schema: MethodTaskSchema,
};

const ReasonsTask: TaskSchemaConfig = {
  id: 'reason',
  schema: ReasonsTaskSchema,
};

const ResolutionsTask: TaskSchemaConfig = {
  id: 'resolution',
  schema: ResolutionTaskSchema,
};

const RefundTask: TaskSchemaConfig = {
  id: 'refund',
  schema: RefundTaskSchema,
};

const ReturnPolicyTask: TaskSchemaConfig = {
  id: 'returnPolicy',
  schema: ReturnPolicyTaskSchema,
};

const SignatureTask: TaskSchemaConfig = {
  id: 'signature',
  schema: SignatureTaskSchema,
};

const ReviewItemSummaryTask: TaskSchemaConfig = {
  id: 'reviewItemSummary',
  schema: null,
};

const ReviewRefundSummaryTask: TaskSchemaConfig = {
  id: 'reviewRefundSummary',
  schema: null,
};

const OrderlessCustomerTask: TaskSchemaConfig = {
  id: 'orderlessCustomer',
  schema: OrderlessCustomerFormSchema,
};

const OrderlessDisclaimerTaskConfig: TaskSchemaConfig = {
  id: 'orderlessDisclaimer',
  schema: OrderlessDisclaimerTaskSchema,
};

const GiftEmailTaskConfig: TaskSchemaConfig = {
  id: 'giftEmail',
  schema: GiftEmailTaskSchema,
};

/**
 * Stands in for a served task this build cannot draw. `schema: null` because it collects
 * nothing — the same shape a content block uses — so it never joins the form, never
 * contributes an error and never blocks the step.
 */
const UnsupportedTask: TaskSchemaConfig = {
  id: 'unsupported',
  schema: null,
};

// Step Configurations

const ItemSelectionStep: StepBaseConfig = {
  id: 'itemSelection',
  route: '/$retailer/item-selection',
};

const ReasonsStep: StepBaseConfig = {
  id: 'reasons',
  route: '/$retailer/reasons',
};

const MethodStep: StepBaseConfig = {
  id: 'returnMethod',
  route: '/$retailer/method',
};

const ReviewStep: StepBaseConfig = {
  id: 'review',
  route: '/$retailer/review',
};

const OrderlessStep: StepBaseConfig = {
  id: 'orderless',
  route: '/$retailer/orderless',
};

// Workflow Maps

export const workflowTaskMap: Record<Tasks, TaskSchemaConfig> = {
  itemSelection: ItemSelectionTask,
  reason: ReasonsTask,
  resolution: ResolutionsTask,
  returnMethod: MethodTask,
  refund: RefundTask,
  reviewItemSummary: ReviewItemSummaryTask,
  reviewRefundSummary: ReviewRefundSummaryTask,
  returnPolicy: ReturnPolicyTask,
  signature: SignatureTask,
  orderlessCustomer: OrderlessCustomerTask,
  orderlessDisclaimer: OrderlessDisclaimerTaskConfig,
  giftEmail: GiftEmailTaskConfig,
  unsupported: UnsupportedTask,
} as const;

export const workflowStepMap: Record<Steps, StepBaseConfig> = {
  itemSelection: ItemSelectionStep,
  reasons: ReasonsStep,
  returnMethod: MethodStep,
  review: ReviewStep,
  orderless: OrderlessStep,
} as const;

// Validation

/**
 * A step's own precondition, expressed the way every other failure is so that it lands
 * in `errors` rather than being dropped. Previously thrown as a plain `Error`, which the
 * catch below silently discarded — leaving the step valid with no key recorded, which is
 * how a shopper with an emptied cart got placed past item selection on resume.
 *
 * Item-scoped only. The one orderless tenant's whole flow is a cart-scoped task with no
 * items by definition, so gating it on a selection made every step of that flow fail —
 * and the dropped error is what hid that failure.
 */
export const noItemsSelectedMessage = 'No items selected';

const noItemsSelected = () =>
  new z.ZodError([
    {
      code: z.ZodIssueCode.custom,
      message: noItemsSelectedMessage,
      path: [],
      input: undefined,
    },
  ]);

const schemaDefect = () =>
  new z.ZodError([
    {
      code: z.ZodIssueCode.custom,
      message: 'Validation could not be completed',
      path: [],
      input: undefined,
    },
  ]);

/**
 * A guard naming a task no step declares.
 *
 * Carries its own message so a test can tell it apart from a schema that threw, which
 * otherwise records an identical error.
 */
export const unresolvableGuardMessage = 'Task guard cannot be resolved';

/**
 * Recorded rather than skipped, unlike a guard the shopper simply has not satisfied.
 * That one clears when they answer it; this one never clears, so skipping it would leave
 * the step permanently passable with the task's data never collected — and the resume
 * walk advances through every valid step, placing the shopper beyond it.
 *
 * Silent, because the form resolver re-runs this on every keystroke for every item. The
 * drift is already reported once per lookup by the guard predicate itself.
 */
const unresolvableGuard = () =>
  new z.ZodError([
    {
      code: 'custom',
      message: unresolvableGuardMessage,
      path: [],
      input: undefined,
    },
  ]);

/**
 * A task the shopper cannot see does not block the step.
 *
 * Guards decide visibility, and before this they decided nothing else — so a
 * guard-withheld task was validated anyway and its failure blocked advancement with no
 * field on screen to fix, judging the shopper on a question they were never asked.
 *
 * Takes the whole flow because a guard routinely names a task on an earlier page and
 * cannot be resolved from `step` alone.
 */
export const validateStep = (
  step: StepConfig,
  data: WorkflowData | null,
  config: Workflow,
  taskRuntimeContext?: TaskRuntimeContext,
): Record<string, z.ZodError | null> => {
  const errors: Record<string, z.ZodError | null> = {};

  const tasksToValidate = step.tasks.filter((task) =>
    isTaskEnabled(task, taskRuntimeContext),
  );

  // Whether the cart is empty is a rule about the step, so it cannot be left to
  // the per-task loop: every item-scoped task withholds its guard on an empty
  // cart, and a step whose item tasks are *all* guarded would skip every one of
  // them and read valid with nothing selected.
  const itemTasksNeedingSelection = tasksToValidate.filter(
    (task) => task.scope !== 'cart' && task.schema,
  );

  for (const task of tasksToValidate) {
    const { schema } = task;

    // Left out of `errors` entirely rather than recorded as null, which
    // `isStepValid` already reads as valid.
    if (!schema) {
      continue;
    }

    try {
      if (task.scope === 'cart') {
        const outcome = resolveTaskGuards(
          task,
          data,
          config,
          undefined,
          taskRuntimeContext,
        );

        if (outcome === 'unsatisfied') continue;
        if (outcome === 'unresolvable') throw unresolvableGuard();

        // Whether any item is selected says nothing about a cart-level value, and an
        // absent one is the schema's business — `undefined` fails a required schema and
        // passes an optional one, which is the same answer a present value would get.
        schema.parse(data?.[task.id as keyof WorkflowData]);
      } else {
        // Asked without an item, because whether a guard names a task no step declares
        // is a property of the config and not of the cart. Asked per item it would go
        // unnoticed on an empty one — no item to ask about, so nothing to report — and
        // the step would read valid with the task's data never collected.
        if (
          resolveTaskGuards(
            task,
            data,
            config,
            undefined,
            taskRuntimeContext,
          ) === 'unresolvable'
        ) {
          throw unresolvableGuard();
        }

        // Per item rather than per task: a guard can be satisfied for one item and
        // not another, and the item that does show the field is still judged on it.
        const judgedItemIds = Object.keys(data?.items ?? {}).filter(
          (itemId) =>
            resolveTaskGuards(
              task,
              data,
              config,
              itemId,
              taskRuntimeContext,
            ) === 'satisfied',
        );

        // Asked before the selection precondition, which is a rule about the step. A
        // task withheld across the whole cart records nothing either way, and an empty
        // cart withholds every item-scoped guard — so throwing first would attribute a
        // step-level failure to a task the shopper cannot see.
        if (hasGuards(task) && judgedItemIds.length === 0) continue;

        if (!hasItemsSelected(data) || !data?.items) {
          throw noItemsSelected();
        }

        for (const itemId of judgedItemIds) {
          // `undefined` for an absent key, because that is the only value all three
          // paths can carry: an unguarded task is judged by the declared
          // `z.object(itemSchemaShape)`, where a missing key is structurally
          // `undefined` and cannot be made `null`. Feeding `null` here aligned this
          // gate with the refinement and left the declared shape disagreeing with
          // both — live the first time a task schema is top-level `.optional()`,
          // which accepts `undefined` and rejects `null`.
          const taskData =
            data.items[itemId] && task.id in data.items[itemId]
              ? (data.items[itemId] as Record<Tasks, unknown>)[task.id]
              : undefined;

          schema.parse(taskData);
        }
      }
      errors[task.id] = null;
    } catch (error) {
      if (error instanceof z.ZodError) {
        errors[task.id] = error;
        continue;
      }

      // A schema that throws something other than a validation error is a defect, not
      // a shopper mistake. Recording it keeps the step invalid — dropping it used to
      // leave the step valid, so the flow advanced past a rule that never ran.
      clientLogger.logError(error, 'Task schema threw a non-validation error', {
        taskId: task.id,
      });
      errors[task.id] = schemaDefect();
    }
  }

  // Recorded against the first such task, which is the one whose field the
  // shopper would use to select an item — unlike the guard-withheld tasks that
  // were skipped above, so the message still lands somewhere they can act on.
  const firstItemTask = itemTasksNeedingSelection[0];
  if (
    firstItemTask &&
    !hasItemsSelected(data) &&
    itemTasksNeedingSelection.every((task) => errors[task.id] == null)
  ) {
    errors[firstItemTask.id] = noItemsSelected();
  }

  return errors;
};

export const isStepValid = (
  errors: Record<string, z.ZodError | null>,
  step: StepConfig,
  taskRuntimeContext?: TaskRuntimeContext,
): boolean =>
  step.tasks.every((task) => {
    if (!isTaskEnabled(task, taskRuntimeContext)) return true;
    return errors[task.id] == null; // null or undefined
  });

/** Keyed by task id, so a stray key is a compile error rather than a silent no-op. */
type TaskSchemaShape = Partial<Record<Tasks, z.ZodType>>;

/**
 * A task with no rule is not a field. Giving it a permissive schema instead would put
 * it in the form and require a key in the data for the parse to succeed.
 */
const toSchemaShape = (tasks: TaskConfig[]): TaskSchemaShape =>
  tasks.reduce<TaskSchemaShape>((acc, task) => {
    if (task.schema) {
      acc[task.id] = task.schema;
    }
    return acc;
  }, {});

const hasGuards = (task: TaskConfig): boolean => (task.guards ?? []).length > 0;

/**
 * Adds a guarded task's failure at the field it belongs to.
 *
 * Re-issued as `custom` rather than forwarded: the original issue's `code` carries a
 * shape the outer schema never declared, and only the message and path are needed for
 * the form to draw the error on the right field.
 */
const addIssuesAt = (
  ctx: z.RefinementCtx,
  path: (string | number)[],
  error: z.ZodError,
) => {
  for (const issue of error.issues) {
    ctx.addIssue({
      code: 'custom',
      message: issue.message,
      path: [...path, ...issue.path],
    });
  }
};

export const createSchemaFromTasks = (
  tasks: TaskConfig[],
  existingData?: WorkflowData | null,
  config?: Workflow,
  taskRuntimeContext?: TaskRuntimeContext,
) => {
  const itemTasks = tasks.filter(
    (task) => task.scope === 'item' && isTaskEnabled(task, taskRuntimeContext),
  );
  // A guarded task is left out of the declared shape and enforced in the refinement
  // below instead. The item shape is one object applied to every key in the record, so a
  // task the shopper sees for one item and not another cannot be expressed as a shape at
  // all — and this schema is what disables Continue, so declaring it unconditionally
  // blocks the step on a field that is not on the page.
  const guardedItemTasks = config ? itemTasks.filter(hasGuards) : [];
  const itemSchemaShape = toSchemaShape(
    config ? itemTasks.filter((task) => !hasGuards(task)) : itemTasks,
  );

  // Preserve itemSelection data if it exists but isn't in current step
  if (existingData?.items && !itemSchemaShape.itemSelection) {
    const hasItemSelectionData = Object.values(existingData.items).some(
      (itemData) => itemData.itemSelection,
    );
    if (hasItemSelectionData) {
      itemSchemaShape.itemSelection = ItemSelectionTaskSchema;
    }
  }

  const cartTasks = tasks.filter(
    (task) => task.scope === 'cart' && isTaskEnabled(task, taskRuntimeContext),
  );
  const guardedCartTasks = config ? cartTasks.filter(hasGuards) : [];
  const cartSchemaShape = toSchemaShape(
    config ? cartTasks.filter((task) => !hasGuards(task)) : cartTasks,
  );

  // Create the base schema with cart items
  const baseSchema: Record<string, z.ZodType> = { ...cartSchemaShape };

  // Only include the items field if there are item tasks. passthrough() keeps
  // item data from earlier steps (e.g. reason/resolution) that the current
  // step doesn't validate — without it the zodResolver strips those keys on
  // submit, wiping selections made on a prior step (e.g. the standalone
  // return-method step dropping reason/resolution before review).
  //
  // A page whose only item-scoped tasks are content blocks contributes no shape, so
  // this keys off the tasks rather than the shape: otherwise `items` is left undeclared
  // and the top-level object strips every item on submit. An empty passthrough shape
  // validates nothing and keeps everything.
  //
  // No served flow produces that page today. Note this reads `mode` before `schema`:
  // `isTaskEnabled` is an equality against `enabled`, so the live tenant's `implicit`
  // item task is filtered out before its rules are ever consulted — its step therefore
  // has no rule-bearing item task as far as these gates are concerned, and its empty
  // cart is not held by them. That is the intended reading of `implicit` (carried, not
  // validated; the cart is populated upstream) rather than a gap to close here.
  //
  // Kept because the pages are becoming configurable, and the failure it prevents is
  // submitting a return with an empty cart from a form that looked valid.
  if (itemTasks.length > 0 || Object.keys(itemSchemaShape).length > 0) {
    baseSchema.items = z.record(
      z.string(),
      z.object(itemSchemaShape).passthrough(),
    );
  }

  const hasGuardedTasks =
    guardedItemTasks.length > 0 || guardedCartTasks.length > 0;

  // `items` is a record, and a record accepts `{}` — so a step that asks the shopper to
  // pick items reads valid with nothing picked. The step needs the refinement to say so,
  // whether or not anything on it is guarded.
  //
  // This used to be reached only when a guarded task existed, which tied the rule to a
  // condition it has nothing to do with. `validateStep` blocked the empty cart either
  // way, but `SubmitButton` gates on `formState.isValid` alone and calls `submit()`
  // directly — so on the last step of an unguarded flow the form was the only gate, and
  // it passed. The served config has one guard, so most steps took that path.
  const [selectionRequiredBy] = itemTasks.filter((task) => task.schema);

  if (!hasGuardedTasks && !selectionRequiredBy) {
    return z.object(baseSchema);
  }

  // The refinement below reads two kinds of key the declared shape does not name: a
  // guard living on an earlier page, and the guarded task's own value, which was pulled
  // out of the shape precisely so it would not block the step. `z.object` strips both,
  // which would leave every guarded task judged against `undefined` forever. Passing
  // them through is what the item shape already does above, and for the same reason.
  //
  // Only where guards need it. A step that reaches the refinement solely for the
  // selection rule keeps stripping unknown keys, as it always has.
  const stepSchema = hasGuardedTasks
    ? z.object(baseSchema).passthrough()
    : z.object(baseSchema);

  // Guards are read off the value under validation rather than off `existingData`,
  // because a guard is satisfied by what the shopper has typed on this page and the form
  // re-validates on every change.
  return stepSchema.superRefine((value, ctx) => {
    const data = value as WorkflowData;

    // The same step-level rule the validator applies, for the same reason the
    // loops below are asked without an item: `items` as a record accepts `{}`,
    // and a guard-withheld task judges nobody — so a step whose item tasks are
    // all guarded would read valid with nothing selected, which on the last
    // step submits.
    if (selectionRequiredBy && !hasItemsSelected(data)) {
      addIssuesAt(ctx, [selectionRequiredBy.id], noItemsSelected());
    }

    // Without the flow there is nothing to resolve a guard against, and both guarded
    // lists are empty by construction above — the selection rule is all this step has.
    if (!config) return;

    for (const task of guardedCartTasks) {
      if (!task.schema) continue;

      const outcome = resolveTaskGuards(
        task,
        data,
        config,
        undefined,
        taskRuntimeContext,
      );

      if (outcome === 'unsatisfied') continue;
      if (outcome === 'unresolvable') {
        addIssuesAt(ctx, [task.id], unresolvableGuard());
        continue;
      }

      const result = task.schema.safeParse(data[task.id as keyof WorkflowData]);
      if (!result.success) addIssuesAt(ctx, [task.id], result.error);
    }

    for (const task of guardedItemTasks) {
      if (!task.schema) continue;

      // Asked without an item, as the validator asks it, so that the two gates reach the
      // same verdict on an empty cart: per item there is nobody to ask, and the form
      // would pass a step the validator blocks — which on the last step submits.
      if (
        resolveTaskGuards(task, data, config, undefined, taskRuntimeContext) ===
        'unresolvable'
      ) {
        addIssuesAt(ctx, [task.id], unresolvableGuard());
        continue;
      }

      for (const itemId of Object.keys(data.items ?? {})) {
        if (
          resolveTaskGuards(task, data, config, itemId, taskRuntimeContext) !==
          'satisfied'
        ) {
          continue;
        }

        const itemData = data.items?.[itemId] as
          | Record<string, unknown>
          | undefined;
        // `undefined` for an absent key, matching `validateStep` and the declared
        // shape an unguarded task is judged by — see the note there. On the last step
        // the form is the only gate that runs, so a disagreement resolves in favour of
        // submitting.
        const taskData =
          itemData && task.id in itemData ? itemData[task.id] : undefined;
        const result = task.schema.safeParse(taskData);
        if (!result.success) {
          addIssuesAt(ctx, ['items', itemId, task.id], result.error);
        }
      }
    }
  });
};
